import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../utils/api";
import { useApp } from "../context/AppContext";
import { t } from "../utils/i18n";
import AvailabilityModal from "../components/AvailabilityModal";

const DAYS = ["Dimanche", "Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi"];
// Montants proposés au défilement plutôt qu'un clavier numérique — plus rapide pour le
// créateur, même principe que les puces de durée déjà présentes sur cet écran.
const PRICE_PRESETS = [5, 10, 15, 20, 25, 30, 40, 50, 60, 75, 100, 150, 200];

type ServiceOption = { id: string; label: string; duration_minutes: number | null; price: string | number | null };
type Service = { id: string; category: string; name: string; base_duration_minutes: number; base_price: string | number | null; at_client_address: boolean; options: ServiceOption[] };
type Staff = { id: string; user_id: string; name: string; avatar_url: string | null; provides_services: boolean };
type StaffSlot = { id: string; day_of_week: number; start_time: string; end_time: string };

const card: React.CSSProperties = { backgroundColor: "white", borderRadius: 16, padding: 20, marginBottom: 14, border: "1px solid #F0F0F0" };
const smallBtn: React.CSSProperties = { padding: "7px 12px", borderRadius: 8, border: "1px solid #E0E0E0", backgroundColor: "#F8F8F8", fontSize: 12.5, fontWeight: 600, color: "#333", cursor: "pointer" };
const primaryBtn: React.CSSProperties = { padding: "10px 16px", borderRadius: 10, border: "none", backgroundColor: "#0a0a0a", color: "white", fontSize: 13, fontWeight: 600, cursor: "pointer" };
const input: React.CSSProperties = { width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid #E0E0E0", fontSize: 14, boxSizing: "border-box", marginBottom: 10 };

// Puces de montants à défiler — remplace le clavier numérique et la mention "Optionnel" par un
// choix rapide parmi des montants courants. `firstChipLabel` couvre les deux sens d'un prix vide
// selon le contexte : gratuit (nouvelle prestation) ou hérite de la prestation de base (option).
function PriceChips({ value, onChange, firstChipLabel }: { value: string; onChange: (v: string) => void; firstChipLabel: string }) {
  return (
    <div className="oltodo-no-scrollbar" style={{ display: "flex", gap: 6, overflowX: "auto", marginBottom: 10, paddingBottom: 2 }}>
      <button onClick={() => onChange("")} style={{ flexShrink: 0, padding: "7px 12px", borderRadius: 16, fontSize: 12, fontWeight: 600, cursor: "pointer", border: value === "" ? "1.5px solid #0a0a0a" : "1.5px solid #E0E0E0", backgroundColor: value === "" ? "#0a0a0a" : "white", color: value === "" ? "white" : "#666" }}>
        {firstChipLabel}
      </button>
      {PRICE_PRESETS.map(p => (
        <button key={p} onClick={() => onChange(String(p))} style={{ flexShrink: 0, padding: "7px 12px", borderRadius: 16, fontSize: 12, fontWeight: 600, cursor: "pointer", border: value === String(p) ? "1.5px solid #0a0a0a" : "1.5px solid #E0E0E0", backgroundColor: value === String(p) ? "#0a0a0a" : "white", color: value === String(p) ? "white" : "#666" }}>
          {p} €
        </button>
      ))}
    </div>
  );
}

// Écran d'admin "Agenda nouvelle génération" — catalogue de prestations (catégorie > prestations
// > options qui écrasent durée/prix) + collaborateurs (horaires de présence, prestations
// autorisées). Un lieu sans prestation garde un accès direct à l'ancien AvailabilityModal (mode
// simple, inchangé) via le bandeau en haut — jamais de migration forcée. Voir le plan
// "Agenda de lieu nouvelle génération" et utils/venueSlots.ts côté serveur.
export default function VenueAgenda() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { lang } = useApp();
  const [venue, setVenue] = useState<any>(null);
  const [tab, setTab] = useState<"services" | "staff">("services");
  const [services, setServices] = useState<Service[]>([]);
  const [staff, setStaff] = useState<Staff[]>([]);
  const [loading, setLoading] = useState(true);
  const [showLegacyModal, setShowLegacyModal] = useState(false);

  const [showServiceForm, setShowServiceForm] = useState<{ category: string } | Service | null>(null);
  const [svcName, setSvcName] = useState("");
  const [svcCategory, setSvcCategory] = useState("");
  const [svcDuration, setSvcDuration] = useState("30");
  const [svcPrice, setSvcPrice] = useState("");
  const [svcAtClientAddress, setSvcAtClientAddress] = useState(false);
  const [savingService, setSavingService] = useState(false);

  const [optionFormFor, setOptionFormFor] = useState<Service | null>(null);
  const [optLabel, setOptLabel] = useState("");
  const [optDuration, setOptDuration] = useState("");
  const [optPrice, setOptPrice] = useState("");
  const [savingOption, setSavingOption] = useState(false);

  const [hoursFor, setHoursFor] = useState<Staff | null>(null);
  const [restrictFor, setRestrictFor] = useState<Staff | null>(null);
  const [restrictSelection, setRestrictSelection] = useState<Set<string>>(new Set());
  const [savingRestriction, setSavingRestriction] = useState(false);

  const load = () => {
    if (!id) return;
    Promise.all([
      api.get(`/venues/${id}`),
      api.get(`/venues/${id}/services`),
      api.get(`/venues/${id}/staff`),
    ]).then(([v, s, st]) => {
      if (v?.venue) setVenue(v.venue);
      setServices(s?.services || []);
      setStaff(st?.staff || []);
    }).finally(() => setLoading(false));
  };
  useEffect(load, [id]);

  const categories = Array.from(new Set(services.map(s => s.category)));

  const openNewService = (category: string) => {
    setSvcName(""); setSvcCategory(category); setSvcDuration("30"); setSvcPrice(""); setSvcAtClientAddress(false);
    setShowServiceForm({ category });
  };
  const openEditService = (s: Service) => {
    setSvcName(s.name); setSvcCategory(s.category); setSvcDuration(String(s.base_duration_minutes)); setSvcPrice(s.base_price != null ? String(s.base_price) : ""); setSvcAtClientAddress(!!s.at_client_address);
    setShowServiceForm(s);
  };
  const saveService = () => {
    if (!svcName.trim() || !svcCategory.trim() || !id) return;
    setSavingService(true);
    const body = { category: svcCategory.trim(), name: svcName.trim(), base_duration_minutes: Number(svcDuration) || 30, base_price: svcPrice ? Number(svcPrice) : null, at_client_address: svcAtClientAddress };
    const isEdit = showServiceForm && "id" in showServiceForm;
    const req = isEdit ? api.put(`/venues/services/${(showServiceForm as Service).id}`, body) : api.post(`/venues/${id}/services`, body);
    req.then(data => {
      if (data?.error) { alert(data.error); return; }
      setShowServiceForm(null);
      load();
    }).catch(() => alert(t(lang, "error_connection"))).finally(() => setSavingService(false));
  };
  const deleteService = (s: Service) => {
    if (!window.confirm(`${t(lang, "delete")} "${s.name}" ?`)) return;
    api.delete(`/venues/services/${s.id}`).then(() => load()).catch(() => alert(t(lang, "error_connection")));
  };

  const openNewOption = (s: Service) => { setOptLabel(""); setOptDuration(""); setOptPrice(""); setOptionFormFor(s); };
  const saveOption = () => {
    if (!optLabel.trim() || !optionFormFor) return;
    setSavingOption(true);
    api.post(`/venues/services/${optionFormFor.id}/options`, {
      label: optLabel.trim(),
      duration_minutes: optDuration ? Number(optDuration) : null,
      price: optPrice ? Number(optPrice) : null,
    }).then(data => {
      if (data?.error) { alert(data.error); return; }
      setOptionFormFor(null);
      load();
    }).catch(() => alert(t(lang, "error_connection"))).finally(() => setSavingOption(false));
  };
  const deleteOption = (o: ServiceOption) => {
    if (!window.confirm(`${t(lang, "delete")} "${o.label}" ?`)) return;
    api.delete(`/venues/service-options/${o.id}`).then(() => load()).catch(() => alert(t(lang, "error_connection")));
  };

  const toggleProvidesServices = (s: Staff) => {
    api.put(`/venues/members/${s.id}/provides-services`, { provides_services: !s.provides_services })
      .then(() => load()).catch(() => alert(t(lang, "error_connection")));
  };

  const openRestrict = (s: Staff) => {
    api.get(`/venues/staff/${s.id}/services`).then(data => {
      const ids: string[] = data?.service_ids || [];
      setRestrictSelection(ids.length > 0 ? new Set(ids) : new Set(services.map(sv => sv.id)));
      setRestrictFor(s);
    }).catch(() => alert(t(lang, "error_connection")));
  };
  const saveRestriction = () => {
    if (!restrictFor) return;
    setSavingRestriction(true);
    api.put(`/venues/staff/${restrictFor.id}/services`, { service_ids: Array.from(restrictSelection) })
      .then(data => { if (data?.error) { alert(data.error); return; } setRestrictFor(null); })
      .catch(() => alert(t(lang, "error_connection"))).finally(() => setSavingRestriction(false));
  };

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: "#999" }}>{t(lang, "loading_ellipsis")}</div>;
  if (!venue) return <div style={{ padding: 40, textAlign: "center", color: "#999" }}>{t(lang, "agenda_venue_not_found")}</div>;

  return (
    <div style={{ maxWidth: 680, margin: "0 auto", padding: 20 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 4 }}>
        <button onClick={() => navigate(-1)} style={{ border: "none", background: "none", cursor: "pointer", fontSize: 20, color: "#666" }}>←</button>
        <span style={{ fontSize: 20, fontWeight: 700 }}>{t(lang, "agenda_title")}</span>
      </div>
      <div style={{ fontSize: 13, color: "#999", marginBottom: 16, marginLeft: 36 }}>{venue.name}</div>

      <div style={{ display: "flex", gap: 8, marginBottom: 16, marginLeft: 36 }}>
        {[{ k: "services", label: t(lang, "agenda_tab_services") }, { k: "staff", label: t(lang, "agenda_tab_staff") }].map(tabItem => (
          <button key={tabItem.k} onClick={() => setTab(tabItem.k as any)} style={{
            padding: "8px 16px", borderRadius: 20, border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600,
            backgroundColor: tab === tabItem.k ? "#0a0a0a" : "#F0F0F0", color: tab === tabItem.k ? "white" : "#666",
          }}>{tabItem.label}</button>
        ))}
      </div>

      <div style={{ ...card, backgroundColor: "#F8F8F8", border: "1px solid #EFEFEF", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
        <span style={{ fontSize: 12.5, color: "#666" }}>{t(lang, "agenda_simple_mode_hint")}</span>
        <button onClick={() => setShowLegacyModal(true)} style={{ ...smallBtn, flexShrink: 0 }}>{t(lang, "agenda_simple_mode_btn")}</button>
      </div>

      {tab === "services" ? (
        <>
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 4 }}>
            <button onClick={() => openNewService("")} style={primaryBtn}>{t(lang, "agenda_new_category")}</button>
          </div>
          {categories.length === 0 && (
            <div style={{ ...card, textAlign: "center", color: "#999", fontSize: 13 }}>{t(lang, "agenda_no_services")}</div>
          )}
          {categories.map(cat => (
            <div key={cat} style={card}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <span style={{ fontSize: 15, fontWeight: 700 }}>{cat}</span>
                <button onClick={() => openNewService(cat)} style={smallBtn}>{t(lang, "agenda_add_service")}</button>
              </div>
              {services.filter(s => s.category === cat).map(s => (
                <div key={s.id} style={{ borderTop: "1px solid #F5F5F5", paddingTop: 12, marginTop: 12 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 600 }}>{s.name}</div>
                      <div style={{ fontSize: 12.5, color: "#999", marginTop: 2 }}>
                        {s.base_duration_minutes} min{s.base_price != null ? `, ${Number(s.base_price).toFixed(2)} €` : ""}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                      <button onClick={() => openEditService(s)} style={smallBtn}>{t(lang, "edit")}</button>
                      <button onClick={() => deleteService(s)} style={smallBtn}>{t(lang, "delete")}</button>
                    </div>
                  </div>
                  <div style={{ marginTop: 8, paddingLeft: 4 }}>
                    {s.options.map(o => (
                      <div key={o.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", fontSize: 12.5, color: "#666" }}>
                        <span>{o.label}, {o.duration_minutes != null ? `${o.duration_minutes} min` : `${s.base_duration_minutes} min`}{o.price != null ? `, ${Number(o.price).toFixed(2)} €` : s.base_price != null ? `, ${Number(s.base_price).toFixed(2)} €` : ""}</span>
                        <button onClick={() => deleteOption(o)} style={{ border: "none", background: "none", cursor: "pointer", color: "#999", fontSize: 11.5 }}>{t(lang, "remove")}</button>
                      </div>
                    ))}
                    <button onClick={() => openNewOption(s)} style={{ border: "none", background: "none", cursor: "pointer", color: "#0a0a0a", fontSize: 12, fontWeight: 600, padding: "6px 0" }}>{t(lang, "agenda_add_option")}</button>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </>
      ) : (
        <>
          {staff.length === 0 && <div style={{ ...card, textAlign: "center", color: "#999", fontSize: 13 }}>{t(lang, "agenda_no_staff")}</div>}
          {staff.map(s => (
            <div key={s.id} style={card}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 40, height: 40, borderRadius: "50%", overflow: "hidden", backgroundColor: "#0a0a0a", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontWeight: 700, fontSize: 13 }}>
                  {s.avatar_url ? <img src={s.avatar_url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : (s.name || "?")[0]}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>{s.name}</div>
                  <div style={{ fontSize: 12, color: "#999" }}>{s.provides_services ? t(lang, "agenda_provides_services") : t(lang, "agenda_no_provides_services")}</div>
                </div>
                <label style={{ position: "relative", display: "inline-block", width: 40, height: 24, flexShrink: 0 }}>
                  <input type="checkbox" checked={s.provides_services} onChange={() => toggleProvidesServices(s)} style={{ opacity: 0, width: 0, height: 0 }} />
                  <span onClick={() => toggleProvidesServices(s)} style={{ position: "absolute", inset: 0, backgroundColor: s.provides_services ? "#0a0a0a" : "#E0E0E0", borderRadius: 12, cursor: "pointer", transition: "0.2s" }} />
                  <span style={{ position: "absolute", top: 3, left: s.provides_services ? 19 : 3, width: 18, height: 18, backgroundColor: "white", borderRadius: "50%", transition: "0.2s", pointerEvents: "none" }} />
                </label>
              </div>
              {s.provides_services && (
                <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                  <button onClick={() => setHoursFor(s)} style={smallBtn}>{t(lang, "agenda_hours_btn")}</button>
                  <button onClick={() => openRestrict(s)} style={smallBtn} disabled={services.length === 0}>{t(lang, "agenda_restrict_btn")}</button>
                </div>
              )}
            </div>
          ))}
        </>
      )}

      {/* Formulaire prestation — même catégorie pré-remplie qu'on ajoute une nouvelle catégorie
          (aucune ligne existante) ou une prestation de plus à une catégorie existante. */}
      {showServiceForm && (
        <Overlay onClose={() => setShowServiceForm(null)}>
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 14 }}>{"id" in showServiceForm ? t(lang, "agenda_edit_service_title") : t(lang, "agenda_new_service_title")}</div>
          <label style={{ fontSize: 11, fontWeight: 700, color: "#999", textTransform: "uppercase" }}>{t(lang, "agenda_category_label")}</label>
          <input value={svcCategory} onChange={e => setSvcCategory(e.target.value)} placeholder="Ex : Brushing" style={input} />
          <label style={{ fontSize: 11, fontWeight: 700, color: "#999", textTransform: "uppercase" }}>{t(lang, "agenda_service_name_label")}</label>
          <input value={svcName} onChange={e => setSvcName(e.target.value)} placeholder="Ex : Shampoing + brushing + cheveux courts" style={input} />
          <label style={{ fontSize: 11, fontWeight: 700, color: "#999", textTransform: "uppercase" }}>{t(lang, "agenda_duration_label")}</label>
          <input type="number" min={5} value={svcDuration} onChange={e => setSvcDuration(e.target.value)} style={input} />
          <label style={{ fontSize: 11, fontWeight: 700, color: "#999", textTransform: "uppercase", display: "block", marginBottom: 8 }}>{t(lang, "agenda_price_label")}</label>
          <PriceChips value={svcPrice} onChange={setSvcPrice} firstChipLabel={t(lang, "price_free")} />
          <label style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 6, marginBottom: 4, cursor: "pointer" }}>
            <input type="checkbox" checked={svcAtClientAddress} onChange={e => setSvcAtClientAddress(e.target.checked)} />
            <span style={{ fontSize: 13 }}>{t(lang, "agenda_at_client_address_label")}</span>
          </label>
          <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
            <button onClick={() => setShowServiceForm(null)} style={{ ...smallBtn, flex: 1, padding: "10px" }}>{t(lang, "cancel")}</button>
            <button onClick={saveService} disabled={savingService || !svcName.trim() || !svcCategory.trim()} style={{ ...primaryBtn, flex: 1, opacity: savingService ? 0.6 : 1 }}>{savingService ? "..." : t(lang, "save")}</button>
          </div>
        </Overlay>
      )}

      {optionFormFor && (
        <Overlay onClose={() => setOptionFormFor(null)}>
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>{t(lang, "agenda_new_option_title")}</div>
          <div style={{ fontSize: 12.5, color: "#999", marginBottom: 14 }}>{optionFormFor.name}</div>
          <label style={{ fontSize: 11, fontWeight: 700, color: "#999", textTransform: "uppercase" }}>{t(lang, "agenda_option_label_label")}</label>
          <input value={optLabel} onChange={e => setOptLabel(e.target.value)} placeholder="Ex : Femme" style={input} />
          <label style={{ fontSize: 11, fontWeight: 700, color: "#999", textTransform: "uppercase" }}>{t(lang, "agenda_duration_label")}</label>
          <input type="number" min={5} value={optDuration} onChange={e => setOptDuration(e.target.value)} placeholder={`${optionFormFor.base_duration_minutes} (base)`} style={input} />
          <label style={{ fontSize: 11, fontWeight: 700, color: "#999", textTransform: "uppercase", display: "block", marginBottom: 8 }}>{t(lang, "agenda_price_label")}</label>
          <PriceChips value={optPrice} onChange={setOptPrice} firstChipLabel={t(lang, "price_inherit")} />
          <div style={{ fontSize: 11.5, color: "#bbb", marginBottom: 10 }}>{t(lang, "agenda_option_inherit_hint")}</div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => setOptionFormFor(null)} style={{ ...smallBtn, flex: 1, padding: "10px" }}>{t(lang, "cancel")}</button>
            <button onClick={saveOption} disabled={savingOption || !optLabel.trim()} style={{ ...primaryBtn, flex: 1, opacity: savingOption ? 0.6 : 1 }}>{savingOption ? "..." : t(lang, "add")}</button>
          </div>
        </Overlay>
      )}

      {hoursFor && (
        <StaffHoursModal venueId={id!} staff={hoursFor} onClose={() => setHoursFor(null)} lang={lang} />
      )}

      {restrictFor && (
        <Overlay onClose={() => setRestrictFor(null)}>
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>{t(lang, "agenda_restrict_btn")}</div>
          <div style={{ fontSize: 12.5, color: "#999", marginBottom: 14 }}>{restrictFor.name}{t(lang, "agenda_restrict_hint_suffix")}</div>
          {services.map(s => (
            <label key={s.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", cursor: "pointer" }}>
              <input type="checkbox" checked={restrictSelection.has(s.id)} onChange={() => setRestrictSelection(prev => {
                const next = new Set(prev);
                if (next.has(s.id)) next.delete(s.id); else next.add(s.id);
                return next;
              })} />
              <span style={{ fontSize: 13.5 }}>{s.name} <span style={{ color: "#999" }}>({s.category})</span></span>
            </label>
          ))}
          <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
            <button onClick={() => setRestrictFor(null)} style={{ ...smallBtn, flex: 1, padding: "10px" }}>{t(lang, "cancel")}</button>
            <button onClick={saveRestriction} disabled={savingRestriction} style={{ ...primaryBtn, flex: 1, opacity: savingRestriction ? 0.6 : 1 }}>{savingRestriction ? "..." : t(lang, "save")}</button>
          </div>
        </Overlay>
      )}

      {showLegacyModal && (
        <AvailabilityModal resource={{ type: "venue", id: id!, label: venue.name }} onClose={() => { setShowLegacyModal(false); load(); }} />
      )}
    </div>
  );
}

function Overlay({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.6)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ backgroundColor: "white", borderRadius: 20, padding: 22, width: "100%", maxWidth: 420, maxHeight: "85vh", overflowY: "auto", boxSizing: "border-box" }}>
        {children}
      </div>
    </div>
  );
}

// Horaires de présence d'un collaborateur — délibérément plus simple que l'ancien AvailabilityModal
// (pas de durée/prix : ça vient de la prestation+option choisie au moment de la réservation, voir
// utils/venueSlots.ts côté serveur), même langage visuel.
function StaffHoursModal({ venueId, staff, onClose, lang }: { venueId: string; staff: Staff; onClose: () => void; lang: string }) {
  const [slots, setSlots] = useState<StaffSlot[]>([]);
  const [loading, setLoading] = useState(true);
  const [day, setDay] = useState(1);
  const [start, setStart] = useState("09:00");
  const [end, setEnd] = useState("18:00");
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    api.get(`/venues/${venueId}/availability?staff_member_id=${staff.id}`).then(data => setSlots(data?.availability || [])).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, [venueId, staff.id]);

  const addSlot = () => {
    if (start >= end) { alert(t(lang, "error_title")); return; }
    setSubmitting(true);
    api.post(`/venues/${venueId}/availability`, { day_of_week: day, start_time: start, end_time: end, staff_member_id: staff.id })
      .then(data => { if (data?.availability) setSlots(prev => [...prev, data.availability]); else alert(data?.error || t(lang, "error_title")); })
      .catch(() => alert(t(lang, "error_connection"))).finally(() => setSubmitting(false));
  };
  const removeSlot = (id: string) => {
    setSlots(prev => prev.filter(s => s.id !== id));
    api.delete(`/venues/availability/${id}`).catch(() => {});
  };

  const grouped = DAYS.map((label, dow) => ({ label, dow, items: slots.filter(s => s.day_of_week === dow).sort((a, b) => a.start_time.localeCompare(b.start_time)) }));

  return (
    <Overlay onClose={onClose}>
      <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>{t(lang, "agenda_hours_btn")}, {staff.name}</div>
      <div style={{ fontSize: 12.5, color: "#999", marginBottom: 14 }}>{t(lang, "agenda_hours_hint")}</div>
      {loading ? <div style={{ fontSize: 13, color: "#999" }}>{t(lang, "loading_ellipsis")}</div> : (
        <div style={{ marginBottom: 16 }}>
          {grouped.filter(g => g.items.length > 0).length === 0 && <div style={{ fontSize: 13, color: "#999", marginBottom: 10 }}>{t(lang, "agenda_no_hours")}</div>}
          {grouped.map(g => g.items.length > 0 && (
            <div key={g.dow} style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#666", marginBottom: 4 }}>{g.label}</div>
              {g.items.map(s => (
                <div key={s.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", backgroundColor: "#F8F8F8", borderRadius: 10, marginBottom: 6 }}>
                  <span style={{ fontSize: 13 }}>{s.start_time.slice(0, 5)} – {s.end_time.slice(0, 5)}</span>
                  <button onClick={() => removeSlot(s.id)} style={{ border: "none", background: "none", cursor: "pointer", color: "#c0392b", fontSize: 12, fontWeight: 600 }}>{t(lang, "remove")}</button>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
      <div className="oltodo-no-scrollbar" style={{ display: "flex", gap: 6, overflowX: "auto", marginBottom: 10 }}>
        {DAYS.map((label, dow) => (
          <button key={dow} onClick={() => setDay(dow)} style={{ flexShrink: 0, padding: "7px 12px", borderRadius: 16, fontSize: 12, fontWeight: 600, cursor: "pointer", border: day === dow ? "1.5px solid #0a0a0a" : "1.5px solid #E0E0E0", backgroundColor: day === dow ? "#0a0a0a" : "white", color: day === dow ? "white" : "#666" }}>
            {label.slice(0, 3)}
          </button>
        ))}
      </div>
      <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
        <input type="time" value={start} onChange={e => setStart(e.target.value)} style={{ flex: 1, padding: "10px 12px", borderRadius: 10, border: "1px solid #E0E0E0", fontSize: 14, boxSizing: "border-box" }} />
        <input type="time" value={end} onChange={e => setEnd(e.target.value)} style={{ flex: 1, padding: "10px 12px", borderRadius: 10, border: "1px solid #E0E0E0", fontSize: 14, boxSizing: "border-box" }} />
      </div>
      <button onClick={addSlot} disabled={submitting} style={{ width: "100%", padding: "13px", borderRadius: 12, border: "none", backgroundColor: "#0a0a0a", color: "white", fontSize: 14, fontWeight: 600, cursor: "pointer", opacity: submitting ? 0.6 : 1 }}>
        {submitting ? "..." : t(lang, "agenda_add_hour_btn")}
      </button>
    </Overlay>
  );
}
